import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #hideIconsOnDesktop() {
    return this.dataset.hideIconsOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Handles the toggle event to show/hide linked images.
   */
  #handleToggle = () => {
    const accordionId = this.dataset.accordionId;
    if (!accordionId) return;

    const linkedImages = document.querySelectorAll(`[data-linked-accordion="${accordionId}"]`);
    const isOpen = this.details.open;

    linkedImages.forEach(imageElement => {
      if (!(imageElement instanceof HTMLElement)) return;
      
      if (isOpen) {
        imageElement.style.display = '';
      } else {
        imageElement.style.display = 'none';
      }
    });
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

/**
 * Links pointing at `#<accordion_id>` (e.g. "View all nutrition facts" → `#accordion-main-item-1`)
 * open the matching accordion row and scroll to it, clearing the sticky header.
 */
if (!window.__accordionHashLinks) {
  window.__accordionHashLinks = true;

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.pathname !== window.location.pathname || !link.hash) return;

    const id = decodeURIComponent(link.hash.slice(1));
    const accordions = Array.from(document.querySelectorAll('accordion-custom[data-accordion-id]')).filter(
      (el) => el instanceof HTMLElement && el.dataset.accordionId?.trim() === id
    );
    // Prefer the visible instance when a row is rendered for both mobile and desktop
    const accordion = accordions.find((el) => el instanceof HTMLElement && el.offsetParent !== null) || accordions[0];
    if (!(accordion instanceof HTMLElement)) return;

    event.preventDefault();

    const details = accordion.querySelector('details');
    if (details) details.open = true;

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const top = accordion.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  });
}
